import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, AlertTriangle } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ANSWER_LABELS = {
  start: { ca: "Començar", es: "Empezar" },
  consistency: { ca: "Ser constant", es: "Ser constante" },
  pain: { ca: "El dolor", es: "El dolor" },
  shame: { ca: "La vergonya", es: "La vergüenza" },
  other: { ca: "Una altra cosa", es: "Otra cosa" },
};

const ANSWER_COLORS = {
  start: "#0B2545",
  consistency: "#3D5A80",
  pain: "#C4636E",
  shame: "#D9925E",
  other: "#94A3B8",
};

/**
 * Validation insights dashboard.
 *
 * Reads aggregate counts from /api/insights (no PII) and renders a simple
 * dashboard. Page is unlinked from the rest of the site, excluded from
 * robots.txt, and marked noindex via SEO meta. Useful for the founder to
 * monitor what blockers resonate most with article readers.
 */
export default function Insights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/insights`);
      setData(r.data);
    } catch (err) {
      setError(err?.message || "Error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#0F172A] font-body" data-testid="insights-page">
      <SEO
        title="Validation Insights"
        description="Internal validation insights dashboard"
        path="/insights"
        lang="ca"
        noindex
      />

      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#0B2545] transition"
            data-testid="insights-back"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Inici</span>
          </Link>
          <span className="font-sans-display font-semibold tracking-tight text-[#0B2545] text-sm">
            AI METABOLIC · Insights
          </span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-10 sm:py-14">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[#475569] font-sans-display mb-2">
              Vista interna · Només números agregats
            </p>
            <h1 className="font-sans-display text-3xl sm:text-4xl text-[#0B2545] tracking-tight leading-tight">
              Validation insights
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/insights/seo"
              data-testid="link-to-seo-checklist"
              className="inline-flex items-center gap-2 h-10 px-4 bg-white border border-slate-200 text-[#0B2545] text-sm font-sans-display rounded-md hover:border-[#0B2545] transition"
            >
              SEO checklist
            </Link>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="insights-refresh"
              className="lift-btn inline-flex items-center gap-2 h-10 px-4 bg-[#0B2545] text-[#F9F8F6] text-sm font-sans-display rounded-md hover:bg-[#0a1f3a] disabled:opacity-60"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Actualitzar
            </button>
          </div>
        </div>

        {error && (
          <div className="border border-[#C4636E] bg-[#C4636E]/10 rounded-md p-4 mb-8 flex items-center gap-3" data-testid="insights-error">
            <AlertTriangle className="h-4 w-4 text-[#C4636E]" />
            <p className="text-sm text-[#0F172A]">No s'han pogut carregar les dades: {error}</p>
          </div>
        )}

        {!loading && data && (
          <>
            {/* Big numbers */}
            <div className="grid sm:grid-cols-2 gap-4 mb-12">
              <StatCard
                label="Leads totals"
                value={data.total_leads}
                hint="Persones a la waitlist"
                testid="stat-total-leads"
              />
              <StatCard
                label="Respostes del poll"
                value={data.poll_responses}
                hint={data.total_leads > 0
                  ? `${Math.round((data.poll_responses / data.total_leads) * 100)}% dels leads`
                  : "—"}
                testid="stat-poll-responses"
              />
            </div>

            {/* By answer breakdown */}
            <section className="mb-12">
              <h2 className="font-sans-display text-xl text-[#0B2545] tracking-tight mb-1">
                Respostes per categoria
              </h2>
              <p className="text-sm text-[#475569] mb-6">
                Què és el que més els costa avui — segons les persones que han contestat el poll als articles.
              </p>
              <AnswerBars data={data.by_answer} total={data.poll_responses} />
            </section>

            {/* By article breakdown */}
            {Object.keys(data.by_article || {}).length > 0 && (
              <section className="mb-12">
                <h2 className="font-sans-display text-xl text-[#0B2545] tracking-tight mb-1">
                  Respostes per article
                </h2>
                <p className="text-sm text-[#475569] mb-6">
                  Quins articles generen més interacció amb el poll.
                </p>
                <ul className="divide-y divide-slate-200 bg-white border border-slate-200 rounded-lg" data-testid="by-article-list">
                  {Object.entries(data.by_article)
                    .sort((a, b) => b[1] - a[1])
                    .map(([articleId, count]) => (
                      <li key={articleId} className="px-5 py-3 flex items-center justify-between">
                        <span className="font-mono text-xs text-[#475569]">{articleId}</span>
                        <span className="font-sans-display font-semibold text-[#0B2545]">{count}</span>
                      </li>
                    ))}
                </ul>
              </section>
            )}

            <p className="text-xs text-slate-400 mt-12 font-sans-display">
              Vista privada · No indexable · Cap dada personal exposada
            </p>
          </>
        )}

        {loading && !data && (
          <p className="text-[#475569] py-12 text-center">Carregant…</p>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, hint, testid }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-6"
      data-testid={testid}
    >
      <p className="text-xs uppercase tracking-[0.18em] text-[#475569] font-sans-display mb-3">
        {label}
      </p>
      <p className="font-sans-display text-5xl text-[#0B2545] tracking-tight tabular-nums">
        {value}
      </p>
      {hint && (
        <p className="text-xs text-slate-400 mt-2 font-sans-display">{hint}</p>
      )}
    </div>
  );
}

function AnswerBars({ data, total }) {
  const entries = Object.entries(data || {});
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div className="space-y-3" data-testid="answer-bars">
      {entries.map(([key, count]) => {
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
        const widthPct = (count / max) * 100;
        return (
          <div key={key} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`answer-bar-${key}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-sans-display text-[#0F172A] tracking-tight">
                {ANSWER_LABELS[key]?.ca || key}
              </span>
              <span className="text-sm tabular-nums">
                <strong className="text-[#0B2545]">{count}</strong>
                <span className="text-slate-400 ml-2">{pct}%</span>
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${widthPct}%`, backgroundColor: ANSWER_COLORS[key] || "#0B2545" }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
